import { useCallback, useState } from 'react';

/**
 * Which confirmation, if any, is currently on screen.
 *
 * A single value rather than two booleans: two booleans can both be true, and
 * "the discard dialog and the publish dialog are both open" is not a state this
 * layout should be able to reach.
 */
export type PendingConfirmation = 'none' | 'discard' | 'publish';

export type UseConfirmationOptions = {
  readonly hasUnsavedChanges: boolean;
  readonly onCancel?: (() => void) | undefined;
  readonly onSubmit: () => void | Promise<void>;
};

/**
 * The two confirmations the stepper needs, and the rules for when each applies.
 *
 * WHY THIS IS A HOOK
 * ------------------
 * The rules are the interesting part and they are worth stating once:
 *
 *  - Cancel only asks when there is something to lose. A confirmation on a form
 *    the user has not touched is noise, and noise is what teaches people to
 *    click through confirmations without reading them.
 *  - Publish always asks. It is irreversible, and the cost of one extra click is
 *    paid once per document while the cost of an accidental publish is paid
 *    forever.
 *  - Confirming a discard does NOT submit. It calls `onCancel`, which is the
 *    caller's navigation. The layout still does not know about routes.
 *
 * Extracted from the component so the component stays a composition of parts and
 * so these rules are testable without rendering the whole flow.
 */
export function useConfirmation({
  hasUnsavedChanges,
  onCancel,
  onSubmit,
}: UseConfirmationOptions) {
  const [pending, setPending] = useState<PendingConfirmation>('none');

  const requestCancel = useCallback((): void => {
    if (hasUnsavedChanges) {
      setPending('discard');
      return;
    }
    onCancel?.();
  }, [hasUnsavedChanges, onCancel]);

  const requestSubmit = useCallback((): void => {
    setPending('publish');
  }, []);

  const dismiss = useCallback((): void => {
    setPending('none');
  }, []);

  const confirmDiscard = useCallback((): void => {
    setPending('none');
    onCancel?.();
  }, [onCancel]);

  const confirmPublish = useCallback((): void => {
    setPending('none');
    void onSubmit();
  }, [onSubmit]);

  return { pending, requestCancel, requestSubmit, dismiss, confirmDiscard, confirmPublish };
}
